/* eslint-disable lines-around-comment */
import { Site } from '@datadog/browser-rum'
import { ALLOWED_DATADOG_SITES, SENSITIVE_QUERY_KEYS, MAX_INPUT_LENGTH } from './constants'
import { logErrorToRum } from './datadog'

/**
 * Normalizes a Datadog site to an allowed value, defaulting to 'datadoghq.com' if invalid
 */
export const normalizeDatadogSite = (inputSite: string): Site => {
  const normalizedSite = String(inputSite).toLowerCase().trim()

  return (ALLOWED_DATADOG_SITES as readonly string[]).includes(normalizedSite)
    ? (normalizedSite as Site)
    : 'datadoghq.com'
}

/**
 * Builds allowed tracing URLs with automatic localhost inclusion for non-prod environments
 */
export const buildAllowedTracingUrls = (
  env: string,
  baseUrls: Array<string | RegExp>,
  options?: { allowedTracingOrigins?: Array<string | RegExp> }
): Array<string | RegExp> => {
  let urls: Array<string | RegExp> = options?.allowedTracingOrigins || baseUrls.filter(Boolean)

  const envNormalized = String(env).toLowerCase()
  const isProd = envNormalized === 'prod' || envNormalized === 'production'

  if (!isProd) {
    urls = urls.concat([/^https?:\/\/localhost(?::\d+)?$/, /^https?:\/\/127\.0\.0\.1(?::\d+)?$/])
    if (typeof window !== 'undefined' && window.location?.hostname === 'localhost') {
      urls.push(window.location.origin)
    }
  }

  return urls
}

/**
 * Redacts sensitive query parameters from a URL while preserving other parameters
 */
export const redactSensitiveQueryParams = (url: string): string => {
  try {
    const u = new URL(url)
    u.searchParams.forEach((_value, key) => {
      if (SENSITIVE_QUERY_KEYS.some(sensitiveKey => key.toLowerCase().includes(sensitiveKey))) {
        u.searchParams.set(key, '[redacted]')
      }
    })

    return u.toString()
  } catch {
    return url
  }
}

/**
 * Determines if an environment is production
 */
export const isProductionEnvironment = (env?: string): boolean => {
  const normalized = String(env || '').toLowerCase()

  return normalized === 'prod' || normalized === 'production'
}

/**
 * RUM beforeSend handler for PII redaction and error filtering
 */
export const createBeforeSendHandler = (): ((event: any, context?: any) => boolean) => {
  return (event: any) => {
    try {
      // Return early if event is invalid
      if (!event || typeof event !== 'object') {
        return true
      }

      // Filter out Next.js navigation abort errors (expected behavior, not real errors)
      if (event?.type === 'error' && event?.error) {
        const errorMessage = event.error.message || event.error.source || ''

        // Filter out Next.js navigation abort errors specifically
        // Use exact patterns to avoid filtering legitimate AbortErrors from other sources
        const isNextJsRouteAbort =
          // Exact match for known Next.js navigation abort error message
          errorMessage.startsWith('Abort fetching component for route') ||
          // Known Next.js error: "AbortError: Fetching component for route ... was aborted"
          /^AbortError: Fetching component for route .+ was aborted$/.test(errorMessage) ||
          // Known Next.js error: "AbortError: Route change to ... was aborted"
          /^AbortError: Route change to .+ was aborted$/.test(errorMessage)

        if (isNextJsRouteAbort) {
          // These are expected Next.js navigation aborts, not real errors
          return false
        }
      }

      // Redact sensitive query parameters in resource URLs
      // Use defensive checks and avoid modifying if already processed
      if (event?.resource?.url && typeof event.resource.url === 'string') {
        try {
          event.resource.url = redactSensitiveQueryParams(event.resource.url)
        } catch (urlError) {
          // If URL redaction fails, continue without modifying
          console.warn('Failed to redact URL in RUM event:', urlError)
        }
      }

      // Redact long user input text
      if (
        event?.type === 'action' &&
        event?.action?.target?.name === 'input' &&
        event?.action?.target?.text &&
        typeof event.action.target.text === 'string'
      ) {
        if (event.action.target.text.length > MAX_INPUT_LENGTH) {
          event.action.target.text = '[redacted]'
        }
      }

      // Sanitize large or non-serializable payloads
      // Check for common non-serializable types and remove them
      if (event?.context) {
        try {
          // Remove functions, symbols, and circular references
          const sanitizedContext = JSON.parse(JSON.stringify(event.context))
          event.context = sanitizedContext
        } catch {
          // If context can't be serialized, remove it to prevent RUM errors
          delete event.context
        }
      }

      return true
    } catch (error) {
      // If the handler throws an error, log it but don't break RUM
      // Return true to allow the event through rather than blocking all RUM calls
      console.warn('Error in RUM beforeSend handler:', error)

      return true
    }
  }
}

/**
 * AWRTR submission failure context structure
 */
export interface AWRTRSubmissionFailureContext {
  restaurantId: string | number
  formId?: string
  formName?: string
  formVersion?: string
  daypart?: string
  submittedBy?: string
  startedAt?: string
  sections?: Array<{
    name: string
    checked: boolean
    isSaved?: boolean
    hasMedia?: boolean
    fieldsCount?: number
  }>
  errorPayload?: any
  errorCode?: string
  errorMessage?: string
  errorDetails?: string
  httpStatus?: number
  timeElapsedMs?: number
  restaurantName?: string
  timeZone?: string
}

/**
 * Logs AWRTR form submission failure with comprehensive context for debugging
 * This provides structured logging to help identify why submissions fail
 *
 * @param context - Complete context about the failed submission
 * @param error - The error object or message (optional, will be extracted from context if not provided)
 */
export const logAWRTRSubmissionFailure = (context: AWRTRSubmissionFailureContext, error?: Error | string | unknown) => {
  if (typeof window === 'undefined') return

  try {
    // Extract error information
    const errorPayload = context.errorPayload || {}
    const errorMessage =
      context.errorMessage ||
      errorPayload.message ||
      errorPayload.details ||
      (error instanceof Error ? error.message : String(error || 'AWRTR form submission failed'))
    const errorCode = context.errorCode || errorPayload.code || errorPayload.status || 'UNKNOWN'
    const httpStatus = context.httpStatus || errorPayload.statusCode || errorPayload.status || null

    // Build structured context for RUM
    const rumContext: Record<string, any> = {
      // Error classification
      errorType: 'AWRTR_SUBMISSION_FAILURE',
      errorCode: String(errorCode),
      errorMessage: String(errorMessage),
      source: 'awrtr-submission',

      // Form identification
      formId: context.formId || null,
      formName: context.formName || null,
      formVersion: context.formVersion || null,

      // Restaurant context
      restaurantId: String(context.restaurantId),
      restaurantName: context.restaurantName || null,
      timeZone: context.timeZone || null,

      // Submission context
      daypart: context.daypart || null,
      submittedBy: context.submittedBy || null,
      startedAt: context.startedAt || null,

      // HTTP response details
      httpStatus: httpStatus,

      // Section completion status
      sectionsCount: context.sections?.length || 0,
      sectionsChecked: context.sections?.filter(s => s.checked).length || 0,
      sectionsSaved: context.sections?.filter(s => s.isSaved).length || 0,
      sectionsWithMedia: context.sections?.filter(s => s.hasMedia).length || 0,

      // Time tracking
      timeElapsedMs: context.timeElapsedMs || null,

      // Section details (redacted summary)
      sectionsSummary:
        context.sections?.map(s => ({
          name: s.name,
          checked: s.checked,
          isSaved: s.isSaved || false,
          hasMedia: s.hasMedia || false,
          fieldsCount: s.fieldsCount || 0
        })) || [],

      // Error payload (limited to avoid PII)
      errorDetails: context.errorDetails || errorPayload.details || null,
      errorPayloadType: errorPayload ? typeof errorPayload : null,
      hasErrorPayload: !!errorPayload
    }

    // Create error object with descriptive message
    const errorObj =
      error instanceof Error
        ? error
        : new Error(
            `AWRTR Submission Failed: ${errorMessage} (Code: ${errorCode}${httpStatus ? `, Status: ${httpStatus}` : ''})`
          )

    // Log to RUM with comprehensive context
    logErrorToRum(errorObj, rumContext, 'awrtr-submission')
  } catch (logError) {
    // Fallback to console if RUM logging fails
    console.error('Failed to log AWRTR submission failure to RUM:', logError)
    console.error('Original AWRTR submission failure context:', context)
  }
}
